/* Copyright (c) 2025 MOCA Systems, Inc.  All rights reserved. */
import { Component } from '@appCanvasCommon/component-system/Component';
import { ComponentInterface } from '@appCanvasCommon/interfaces/component.interface';
import { CanvasAngularLand } from '../../../../app-data/interfaces/canvas-angular-land';
import { AbstractApplication } from '@appCanvasCommon/core/AbstractApplication';
import { Point, Rectangle } from 'pixi.js-legacy';
import { ExportCapture, ICapturedTile } from '@appCanvasCommon/export/ExportCapture';
import { IPrintConfiguration, RoleLegendOptions } from '../../../../app-data/constants/plan-print.model';
import { FancyContainer } from '@appCanvasCommon/graphics/FancyContainer';
import { Lightbox } from '@appCanvasCommon/export/Lightbox';
import { Role, Ticket } from '@touchplan/common';
import { getTicketsInRect } from 'utils';
import { DestroyOptions } from '@appCanvasCommon/core/DestroyOptions';
import { FancyGraphics } from '@appCanvasCommon/graphics/FancyGraphics';
import { IExportRect } from '@appPlanApplication/export/FancyPrint';
import { HighResolutionText } from '@appCanvasCommon/text/HighResolutionText';
import { FancyDefaultStyles } from '@appCanvasCommon/text/FancyDefaultStyles';
import { PLAN_PRINT_ROLE_LEGEND_LABEL } from '@touchplan/strings';
/**
 * Configuration settings for the role legend appearance and layout
 */
const settings = {
	/**
	 * Base width of the legend box, came from measuring the mock
	 */
	width: 256,
	/**
	 * Horizontal margin applied to left and right sides of the legend
	 */
	leftMargin: 12,
	/**
	 * Vertical margin applied to top and bottom of the legend
	 */
	topMargin: 12,
	/**
	 * Height of each role item row in the legend
	 */
	rowHeight: 24,
	/**
	 * Background color of the legend box (white)
	 */
	backgroundColor: 0xffffff,
	/**
	 * Radius for rounded corners of the legend box
	 */
	cornerRadius: 2,
	/**
	 * Width of the border stroke around the legend box
	 */
	strokeWidth: 1,
	/**
	 * Color of the border stroke (black)
	 */
	strokeColor: 0x000000,
	/**
	 * Radius of the colored circle indicator for each role
	 */
	colorRadius: 8,
	/**
	 * Height reserved for the legend header section
	 */
	legendHeaderHeight: 64,
	/**
	 * Left margin/indent for legend items
	 */
	itemLeftMargin: 24,
	/**
	 * Vertical spacing between role items
	 */
	itemSpacing: 12,
	/**
	 * Text color for role names (black)
	 */
	itemFontColor: 0x000000,
	/**
	 * Font size for role names
	 */
	itemFontSize: 18,
};

/**
 * RoleLegendDrawer generates a new scene graph containing our export role legend
 */
export class RoleLegendDrawer extends Component implements ComponentInterface {
	/**
	 * FancyContainer object representing the root node for our Role Legend
	 * @private
	 */
	private _container!: FancyContainer;
	/**
	 * We use this to draw all of our role legend colors
	 * @private
	 */
	private _graphics!: FancyGraphics;
	/**
	 * Instance of the ExportCapture. Since we are rendering the text to a RenderTexture, we do not need to add our
	 * DisplayObject to a scene graph first.
	 * @private
	 */
	private _capture!: ExportCapture;
	/**
	 * Creates a new ExportCapture instance.
	 * @param application - The application instance
	 * @returns A new ExportCapture instance
	 * @protected
	 */
	protected createExportCapture(application: AbstractApplication): ExportCapture {
		return new ExportCapture(application);
	}
	/**
	 * Invoked at the start of the stitching process. Returns a ICapturedTile that can be used by the stitcher.
	 * @param canvasAngularLand - Angular dependencies, etc
	 * @param application - The AbstractApplication this is attached to
	 * @param lightbox - The rectangle representing the export region
	 * @param config - The current Print Configuration used for the capture
	 * @param exportRect - The rectangle of the final export
	 * @returns ICapturedTile - Returns a ICapturedTile object that can be consumed by the ExportStitcher
	 */
	public onExportDrawNode(
		canvasAngularLand: CanvasAngularLand,
		application: AbstractApplication,
		lightbox: Lightbox,
		config: IPrintConfiguration,
		exportRect: IExportRect
	): ICapturedTile {
		if (!this.hasRoleLegend(config)) {
			return ExportCapture.createEmptyTile();
		}

		if (this._capture) {
			this._capture.purge();
		}
		this._capture = this.createExportCapture(application);

		const tickets: Map<string, Ticket> = canvasAngularLand.tickets.syncList;
		const worldView: Rectangle = lightbox.worldView;
		const visibleTickets: Ticket[] = getTicketsInRect(tickets, worldView);
		const roles: Role[] = this.generateRoleList(visibleTickets, worldView);
		const position: Rectangle = this.getPositionAndSize(roles, config, exportRect);

		this.create();
		this.drawBackground(position);
		this.drawHeader(position);
		this.drawLegend(position, roles);

		return this._capture.capture(
			new Rectangle(0, 0, position.width + settings.strokeWidth, position.height + settings.strokeWidth),
			this._container,
			{
				x: position.x,
				y: position.y,
			}
		);
	}

	/**
	 * Invoked once the export process is completed.
	 * @param canvasAngularLand
	 * @param application
	 */
	public onExportComplete(canvasAngularLand: CanvasAngularLand, application: AbstractApplication): void {
		this._capture.purge();
		this._container.destroy(DestroyOptions.includeChildren);
	}
	/**
	 * Extracts a unique list of roles from the visible tickets and sorts them alphabetically.
	 * @param tickets - Array of tickets visible in the export area
	 * @param worldView - The visible world rectangle (currently unused but available for future filtering)
	 * @returns Role[] - Sorted array of unique roles found in the visible tickets
	 * @private
	 */
	private generateRoleList(tickets: Ticket[], worldView: Rectangle): Role[] {
		const processedRoles: Set<string> = new Set<string>();
		const roles: Role[] = [];
		tickets.forEach((ticket) => {
			if (ticket.view.liveRole && !processedRoles.has(ticket.view.liveRole.$id)) {
				roles.push(ticket.view.liveRole);
				processedRoles.add(ticket.view.liveRole.$id);
			}
		});

		return roles.sort((a: Role, b: Role) =>
			a.view.roleName.toLowerCase().localeCompare(b.view.roleName.toLowerCase())
		);
	}
	/**
	 * Initializes the container and graphics objects for the legend.
	 * @private
	 */
	private create(): void {
		this._container = new FancyContainer();
		this._graphics = new FancyGraphics();
		this._container.addChild(this._graphics);
		// Bit of a hack here. The stroke adds size to our export, so we if do not shift the container by an equal amount
		// there will be a few pixels missing from our capture.
		this._container.x = settings.strokeWidth;
		this._container.y = settings.strokeWidth;
	}
	/**
	 * Draws the background rectangle with border stroke for the legend.
	 * @param position - Rectangle defining the dimensions of the legend box
	 * @private
	 */
	private drawBackground(position: Rectangle): void {
		this._graphics.lineStyle(settings.strokeWidth, settings.strokeColor);
		this._graphics.beginFill(settings.backgroundColor);
		this._graphics.drawRoundedRect(0, 0, position.width, position.height, settings.cornerRadius);
		this._graphics.endFill();
		this._graphics.lineStyle(0);
	}
	/**
	 * Creates and positions the "Role Legend" header text at the top of the legend.
	 * @param position - Rectangle defining the dimensions of the legend box
	 * @private
	 */
	private drawHeader(position: Rectangle): void {
		const text: HighResolutionText = this.createText(PLAN_PRINT_ROLE_LEGEND_LABEL);
		text.x = position.width / 2 - text.width / 2;
		text.y = settings.topMargin;
		this._container.addChild(text);
	}
	/**
	 * Draws the list of roles with their colored indicators and names.
	 * Truncates role names that are too long to fit within the container.
	 * @param position - Rectangle defining the dimensions of the legend box
	 * @param roles - Array of roles to display in the legend
	 * @private
	 */
	private drawLegend(position: Rectangle, roles: Role[]): void {
		let x: number = settings.itemLeftMargin;
		let y: number = settings.legendHeaderHeight;
		const circleSpace: number = settings.colorRadius * 2;
		const maxTextWidth: number =
			position.width -
			settings.itemLeftMargin -
			settings.itemLeftMargin * 2 -
			circleSpace -
			settings.leftMargin;

		roles.forEach((role) => {
			const text: HighResolutionText = this.createText(role.view.roleName);
			if (text.width > maxTextWidth) {
				let truncatedText: string = role.view.roleName;
				const ellipsis: string = '...';
				while (text.width > maxTextWidth && truncatedText.length > 0) {
					truncatedText = truncatedText.slice(0, -1);
					text.text = truncatedText + ellipsis;
				}
			}

			text.x = x + settings.itemLeftMargin * 2;
			text.y = y;
			text.anchor.set(0, 0.5);
			this._container.addChild(text);
			this._graphics.beginFill(role.view.color);
			this._graphics.drawCircle(x, y, settings.colorRadius);
			this._graphics.endFill();
			y += settings.rowHeight + settings.itemSpacing;
		});
	}
	/**
	 * Creates a new text element with the default styling applied.
	 * @param defaultText - The text content to display
	 * @returns HighResolutionText - A styled text object ready to be added to the container
	 * @private
	 */
	private createText(defaultText: string = ``): HighResolutionText {
		const text: HighResolutionText = new HighResolutionText(defaultText, FancyDefaultStyles.create());
		text.style.fill = settings.itemFontColor;
		text.style.fontSize = settings.itemFontSize;
		return text;
	}
	/**
	 * Calculates the dimensions and position of the legend based on the number of roles
	 * and the configured placement option (top-left, top-right, bottom-left, bottom-right).
	 * @param roles - Array of roles to determine the height needed
	 * @param config - Print configuration containing the legend placement option
	 * @param exportRect - The dimensions of the export area for positioning calculations
	 * @returns Rectangle - The calculated position and size for the legend
	 * @private
	 */
	private getPositionAndSize(roles: Role[], config: IPrintConfiguration, exportRect: IExportRect): Rectangle {
		const position: Rectangle = new Rectangle();
		position.width = settings.width + settings.leftMargin * 2;

		const contentHeight: number =
			roles.length * settings.rowHeight + (roles.length - 1) * settings.itemSpacing;
		const totalContentHeight: number = contentHeight + settings.legendHeaderHeight;

		const totalHeightWithMargins: number = totalContentHeight + settings.topMargin * 2;
		position.height = Math.min(totalHeightWithMargins, exportRect.height);

		const legendPosition: Point = this.getPosition(config, exportRect, position.height);
		position.x = legendPosition.x;
		position.y = legendPosition.y;

		return position;
	}
	/**
	 * Determines the x,y coordinates for the legend based on the placement option.
	 * @param config - Print configuration containing the legend placement option
	 * @param exportRect - The dimensions of the export area
	 * @param legendHeight - The calculated height of the legend including margins
	 * @returns Point - The x,y coordinates where the legend should be positioned
	 * @private
	 */
	private getPosition(config: IPrintConfiguration, exportRect: IExportRect, legendHeight: number): Point {
		switch (config.roleLegend) {
			case RoleLegendOptions.TopLeft:
				return new Point(settings.leftMargin, settings.topMargin);

			case RoleLegendOptions.TopRight:
				return new Point(exportRect.width - settings.width - settings.leftMargin, settings.topMargin);

			case RoleLegendOptions.BottomLeft:
				return new Point(settings.leftMargin, exportRect.height - legendHeight - settings.topMargin);

			case RoleLegendOptions.BottomRight:
				return new Point(
					exportRect.width - settings.width - settings.leftMargin,
					exportRect.height - legendHeight - settings.topMargin
				);

			default:
				return new Point(settings.leftMargin, settings.topMargin);
		}
	}
	/**
	 * Checks if the role legend is enabled in the print configuration.
	 * @param config - Print configuration to check
	 * @returns boolean - True if legend should be displayed, false if set to None
	 * @private
	 */
	private hasRoleLegend(config: IPrintConfiguration): boolean {
		return config.roleLegend !== RoleLegendOptions.None;
	}
}
